import { useTranslation } from 'react-i18next';
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';

import './dashboard.style.scss';
import { useMain } from 'contexts/main.context';
import { createKpu, updateKpu, downloadZip } from 'requests/dashboard.request';
import { DictionaryKey, getDictionary } from 'requests/dictionary.request';
import Loader from 'components/loader/loader.component';
import { IKpu, IObject } from 'interfaces/applicant.interface';
import Icon from 'components/icon/icon.component';
import { copyToClipboard } from 'utils/shared.utils';
import Input from 'components/input/input.component';
import Select from 'components/select/select.component';
import { removeEmptyStrings } from 'utils/form.utils';
import IpConnection from './connection-types/ip-connection.component';
import ComPortConnection from './connection-types/com-port-connection.component';
import RestConnection from './connection-types/rest-connection.component';
import TankTable from './tank-table.component';

const Dashboard = () => {
  const { t } = useTranslation();
  const { applicantData, handleGetApplicant } = useMain();
  const [loading, setLoading] = useState(false);
  const [objects, setObjects] = useState<IObject[]>([]);
  const [objectExpanded, setObjectExpanded] = useState<Record<number, boolean>>({});
  const [kpuExpanded, setKpuExpanded] = useState<Record<number, boolean>>({});
  const [dictionaries, setDictionaries] = useState<Record<string, string[]>>({});

  useEffect(() => {
    if (applicantData.objects) {
      setObjects(applicantData.objects);
      if (!objects.length && applicantData.objects.length === 1) {
        handleExpandObject(applicantData.objects[0].id)();
      }
    }
  }, [applicantData.objects]);

  const handleGetKpuClientTypes = () => {
    getDictionary('kpu-client-types')
      .then((res) => {
        const kpuTypes = res.filter(
          (item) => item.toLowerCase() !== 'undefind' && item.toLowerCase() !== 'undefined',
        );
        setDictionaries((prevState) => ({ ...prevState, ['kpu-client-types']: kpuTypes }));
      })
      .catch(() => setDictionaries((prevState) => ({ ...prevState, ['kpu-client-types']: [] })));
  };

  const handleGetDictionary = (key: DictionaryKey) => {
    getDictionary(key)
      .then((res) => setDictionaries((prevState) => ({ ...prevState, [key]: res })))
      .catch(() => setDictionaries((prevState) => ({ ...prevState, [key]: [] })));
  };

  useEffect(() => {
    handleGetKpuClientTypes();
    handleGetDictionary('connection-types');
    handleGetDictionary('oil-product-types');
    handleGetDictionary('volume-unit-types');
    handleGetDictionary('level-unit-types');
    handleGetDictionary('mass-unit-types');
  }, []);

  const handleExpandObject = (objectId: number) => () => {
    setObjectExpanded((prevState) => ({ ...prevState, [objectId]: !prevState[objectId] }));
  };

  const handleExpandKpu = (kpuId: number) => () => {
    setKpuExpanded((prevState) => ({ ...prevState, [kpuId]: !prevState[kpuId] }));
  };

  const handleChangeKpuInput =
    (objectId: number, kpuId: number) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const { name, value } = e.target;
      setObjects((prevItems) =>
        prevItems.map((item) =>
          item.id === objectId
            ? {
                ...item,
                kpus: item.kpus?.map((kpu) =>
                  kpu.kpuId === kpuId ? { ...kpu, [name]: value } : kpu,
                ),
              }
            : item,
        ),
      );
    };

  const handleChangeTankInput =
    (objectId: number, kpuId: number) =>
    (tankId: number, e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const { name, value } = e.target;
      setObjects((prevItems) =>
        prevItems.map((item) =>
          item.id === objectId
            ? {
                ...item,
                kpus: item.kpus?.map((kpu) =>
                  kpu.kpuId === kpuId
                    ? {
                        ...kpu,
                        tanks: kpu.tanks?.map((tank) =>
                          tank.tankId === tankId ? { ...tank, [name]: value } : tank,
                        ),
                      }
                    : kpu,
                ),
              }
            : item,
        ),
      );
    };

  const handleAddKpu = (objectId: number) => () => {
    setLoading(true);
    createKpu(objectId)
      .then((res) => {
        if (res.success) {
          toast.success(t('success.kpuCreated'));
          handleGetApplicant();
        }
      })
      .catch(() => toast.error(t('errors.kpuCreate')))
      .finally(() => setLoading(false));
  };

  const handleSaveKpu =
    ({ tanks, ...kpu }: IKpu) =>
    () => {
      if (
        !kpu.clientType ||
        (kpu.clientType.toLowerCase() === 'undefind' &&
          kpu.clientType.toLowerCase() === 'undefined')
      ) {
        toast.warning(t('warning.selectKpuType'));
        return;
      }
      setLoading(true);
      const dto = removeEmptyStrings(kpu) as IKpu;
      updateKpu(dto)
        .then((res) => {
          if (res.success) {
            toast.success(t('success.kpuSaved'));
          } else {
            toast.error(res.error);
          }
        })
        .catch(() => toast.error(t('errors.kpuSave')))
        .finally(() => setLoading(false));
    };

  const handleDownloadZip = (objectId: number) => () => {
    setLoading(true);
    downloadZip(objectId)
      .then(() => {
        toast(
          t('dashboard.installHint'),
          {
            autoClose: 30000,
            icon: <Icon name={'ChevronUp'} />,
            className: 'dashboard__notification',
          },
        );
      })
      .catch(() => toast.error(t('errors.fileDownload')))
      .finally(() => setLoading(false));
  };

  return (
    <div className="dashboard">
      <Loader loading={loading}>
        {objects.map((object) => (
          <div key={object.id} className="dashboard__object">
            <button
              type="button"
              className="dashboard__object__title"
              onClick={handleExpandObject(object.id)}
            >
              <span>{object.objectName}</span>
              <Icon name={objectExpanded[object.id] ? 'ChevronDown' : 'ChevronRight'} />
            </button>
            {objectExpanded[object.id] ? (
              <div className="dashboard__object__child">
                {object.kpus?.map((kpu) => (
                  <div key={kpu.kpuId} className="dashboard__kpu">
                    <div className="dashboard__kpu__row">
                      <div className="dashboard__kpu__btnCol">
                        <button
                          type="button"
                          className="dashboard__expandBtn"
                          onClick={handleExpandKpu(kpu.kpuId)}
                        >
                          <Icon
                            name={kpuExpanded[kpu.kpuId] ? 'ChevronDown' : 'ChevronRight'}
                            size={27}
                          />
                        </button>
                        <button
                          type="button"
                          className="dashboard__saveBtn"
                          onClick={handleSaveKpu(kpu)}
                          title={String(t('dashboard.save'))}
                        >
                          <Icon name={'Save'} />
                        </button>
                      </div>
                      <div className="dashboard__kpu__grid">
                        <Input
                          name="name"
                          value={kpu.name || ''}
                          label={t('dashboard.name')}
                          onChange={handleChangeKpuInput(object.id, kpu.kpuId)}
                        />
                        <Select
                          label={t('dashboard.kpuType')}
                          name={'clientType'}
                          options={dictionaries['kpu-client-types']}
                          value={kpu.clientType || ''}
                          onChange={handleChangeKpuInput(object.id, kpu.kpuId)}
                        />
                        <Select
                          label={t('dashboard.connectionType')}
                          name={'connectionType'}
                          options={dictionaries['connection-types']}
                          value={kpu.connectionType || ''}
                          onChange={handleChangeKpuInput(object.id, kpu.kpuId)}
                        />
                        {kpu.connectionType === 'Ip' && (
                          <IpConnection
                            kpu={kpu}
                            handleChange={handleChangeKpuInput(object.id, kpu.kpuId)}
                          />
                        )}
                        {kpu.connectionType === 'Com' && (
                          <ComPortConnection
                            kpu={kpu}
                            handleChange={handleChangeKpuInput(object.id, kpu.kpuId)}
                          />
                        )}
                        {kpu.connectionType === 'Rest' && (
                          <RestConnection
                            kpu={kpu}
                            handleChange={handleChangeKpuInput(object.id, kpu.kpuId)}
                          />
                        )}
                      </div>
                    </div>

                    {kpuExpanded[kpu.kpuId] ? (
                      <TankTable
                        object={object}
                        kpu={kpu}
                        dictionaries={dictionaries}
                        handleChange={handleChangeTankInput(object.id, kpu.kpuId)}
                        handleExpandNewKpu={(kpuId: number) => {
                          if (!kpuExpanded[kpuId]) {
                            handleExpandKpu(kpuId)();
                          }
                        }}
                      />
                    ) : null}
                  </div>
                ))}
                <div className="dashboard__kpu__footer">
                  <div className="dashboard__kpu__footer__row">
                    <button
                      type="button"
                      className="dashboard__addBtn"
                      onClick={handleAddKpu(object.id)}
                    >
                      <Icon name={'CirclePlus'} />
                      <span>{t('dashboard.addKpu')}</span>
                    </button>
                    <button
                      type="button"
                      className="dashboard__addBtn"
                      onClick={handleDownloadZip(object.id)}
                    >
                      <Icon name={'Download'} />
                      <span>{t('dashboard.downloadFile')}</span>
                    </button>
                  </div>
                  <div className="dashboard__kpu__footer__token">
                    <strong>{t('dashboard.token')}</strong>
                    <span>{object.oilTrackToken}</span>
                    <button
                      type="button"
                      className="dashboard__addBtn"
                      onClick={() => copyToClipboard(object.oilTrackToken as string)}
                      title={String(t('dashboard.copyToken'))}
                    >
                      <Icon name={'Copy'} size={20} />
                    </button>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        ))}
      </Loader>
    </div>
  );
};

export default Dashboard;
